import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  MenuItem,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { FinancialDataAPI, FinancialGridRow, OccupancyMetricsData } from '../../types/types';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '@services';

interface OccupancyMetricsProps {
  financialData: FinancialDataAPI | null;
  occupancyMetricsData: OccupancyMetricsData | null;
  selectedHotel: string;
  setError: (error: string | null) => void;
  addFormItemName: string;
  addFormYear: string;
  addFormAmount: string;
  saving: boolean;
  gridRows: FinancialGridRow[];
  gridColumns: Array<{
    field: string;
    headerName?: string;
    [key: string]: unknown;
  }>;
  setAddFormItemName: (value: string) => void;
  setAddFormYear: (value: string) => void;
  setAddFormAmount: (value: string) => void;
  handleAddNewItem: () => Promise<void>;
  handleDeleteClick: (row: FinancialGridRow) => void;
  processRowUpdate: (newRow: FinancialGridRow) => Promise<FinancialGridRow>;
  onProcessRowUpdateError: (error: Error) => void;
  loading: boolean;
  fetchOccupancyMetricsData: (hotelId: string) => Promise<void>;
}

const OccupancyMetrics: React.FC<OccupancyMetricsProps> = ({
  financialData,
  occupancyMetricsData,
  selectedHotel,
  setError,
  addFormItemName,
  addFormYear,
  addFormAmount,
  saving,
  gridRows,
  gridColumns,
  setAddFormItemName,
  setAddFormYear,
  setAddFormAmount,
  handleAddNewItem,
  handleDeleteClick,
  processRowUpdate,
  onProcessRowUpdateError,
  loading,
  fetchOccupancyMetricsData
}) => {
  const [stagedDeletions, setStagedDeletions] = useState<Set<string>>(new Set());
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Get years that actually have data from Occupancy metrics
  const allYears = React.useMemo(() => {
    const uniqueYears = new Set<string>();

    if (occupancyMetricsData?.months) {
      Object.values(occupancyMetricsData.months).forEach(monthData => {
        Object.entries(monthData).forEach(([year, value]) => {
          if (value !== undefined && value !== null && value !== 0) {
            uniqueYears.add(year);
          }
        });
      });
    }

    if (financialData?.years && uniqueYears.size === 0) {
      financialData.years.forEach(year => uniqueYears.add(year));
    }

    if (uniqueYears.size === 0) {
      uniqueYears.add(new Date().getFullYear().toString());
    }

    return Array.from(uniqueYears).sort((a, b) => parseInt(a) - parseInt(b));
  }, [occupancyMetricsData, financialData]);

  // Filter out staged (to be deleted) years
  const activeYears = React.useMemo(() => {
    return allYears.filter(year => !stagedDeletions.has(year));
  }, [allYears, stagedDeletions]);

  // Stage a deletion instead of immediately deleting
  const handleStageDeleteYear = (year: string) => {
    if (window.confirm(`Are you sure you want to delete all Occupancy data for ${year}?`)) {
      setStagedDeletions(prev => {
        const newSet = new Set(prev);
        newSet.add(year);
        return newSet;
      });
      setHasPendingChanges(true);
    }
  };

  // Handle saving the changes when years are staged for deletion
  const handleSaveChanges = async () => {
    if (!hasPendingChanges) return;
    setIsSaving(true);
    setError(null);
    try {
      // Process each staged deletion
      const results: Array<{ year: string, deletedCount: number }> = [];
      for (const year of stagedDeletions) {
        const response = await api.delete(`/ocr/hotel/${selectedHotel}/occupancy-metrics/year/${year}`);
        results.push({ year, deletedCount: response.data.updatedCount || 0 });
      }
      setStagedDeletions(new Set());
      setHasPendingChanges(false);
      const totalDeleted = results.reduce((sum, item) => sum + item.deletedCount, 0);
      setError(`Successfully deleted ${totalDeleted} Occupancy metric records`);
      // Refresh data after successful deletion
      await fetchOccupancyMetricsData(selectedHotel);
    } catch (error) {
      console.error('Error saving changes:', error);
      setError(`Failed to save changes: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Filter only relevant rows for Occupancy metrics
  const filteredRows = gridRows.filter(row => row.documentType === 'Occupancy Metrics');

  // Transform Occupancy data for the grid
  const transformedRows = React.useMemo(() => {
    if (occupancyMetricsData && occupancyMetricsData.years && occupancyMetricsData.years.length > 0) {
      return months.map((month) => {
        const monthData = occupancyMetricsData.months[month] || {};

        const row: Record<string, unknown> = {
          id: `Occupancy Metrics-month-${month}`,
          documentType: 'Occupancy Metrics',
          categoryId: null,
          itemName: month,
          isDeleted: false,
          isNew: false,
          originalCategoryId: null
        };

        activeYears.forEach(year => {
          row[year] = monthData[year] || 0;
        });

        return row as FinancialGridRow;
      });
    }

    return filteredRows;
  }, [filteredRows, occupancyMetricsData, months, activeYears]);

  // Define base columns
  const baseColumns = React.useMemo(() => [
    {
      field: 'itemName',
      headerName: 'Month',
      width: 150,
      flex: 1,
      editable: true
    }
  ], []);

  // Add year columns for active years
  const yearColumns = React.useMemo(() => activeYears.map((year: string) => ({
    field: year,
    headerName: year,
    width: 100,
    flex: 0.5,
    editable: true,
    // Percentage formatting
    valueFormatter: (params: any) => {
      if (!params) return "-";
      const value = params.value;
      if (value === null || value === undefined || value === "") return "-";
      const numValue = typeof value === 'number' ? value : parseFloat(String(value));
      if (isNaN(numValue)) return String(value);
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(numValue / 100);
      } catch (err) {
        console.error('Error formatting value:', err);
        return String(value);
      }
    },
    renderCell: (params: any) => {
      if (!params) return "-";
      const value = params.value;
      if (value === null || value === undefined || value === "") return "-";
      try {
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(numValue / 100);
      } catch (err) {
        console.error('Error rendering cell value:', err);
        return String(value);
      }
    },
    renderHeader: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span>{year}</span>
        <IconButton
          onClick={() => handleStageDeleteYear(year)}
          size="small"
          color="error"
          sx={{ ml: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    )
  })), [activeYears, handleStageDeleteYear]);

  // Combine base columns and year columns
  const activeColumns = React.useMemo(() => [...baseColumns, ...yearColumns], [baseColumns, yearColumns]);

  // Calculate statistics
  const calculateStats = React.useMemo(() => {
    if (!transformedRows || transformedRows.length === 0) {
      return { avgOccupancy: 0, maxOccupancy: 0, minOccupancy: 0, dataPoints: 0 };
    }

    const allValues = transformedRows.flatMap(row =>
      activeYears.map(year => Number(row[year]) || 0)
    ).filter(val => val > 0);

    const avgOccupancy = allValues.length > 0 ? allValues.reduce((sum, val) => sum + val, 0) / allValues.length : 0;
    const maxOccupancy = allValues.length > 0 ? Math.max(...allValues) : 0;
    const minOccupancy = allValues.length > 0 ? Math.min(...allValues) : 0;

    return { avgOccupancy, maxOccupancy, minOccupancy, dataPoints: allValues.length };
  }, [transformedRows, activeYears]);

  const theme = useTheme();

  // CSV Export
  const handleExportCSV = () => {
    if (!transformedRows || transformedRows.length === 0) return;

    const headers = activeColumns.map((col: any) => col.headerName || col.field);
    let csv = headers.join(',') + '\n';

    transformedRows.forEach(row => {
      const rowValues = activeColumns.map((col: any) => {
        const value = row[col.field];
        return value === null || value === undefined ? '' : value;
      });
      csv += rowValues.join(',') + '\n';
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + csv;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'occupancy_metrics.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // If no data is found, show empty state
  if (!transformedRows || transformedRows.length === 0) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Occupancy Metrics Guide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Occupancy rate is calculated by dividing the number of rooms sold by the total number of rooms available.
          </Typography>
        </Alert>

        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>No Occupancy Metrics data found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add occupancy data using the form below or upload documents with occupancy information.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Info Section */}
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          📊 Occupancy Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track monthly occupancy rates to analyze hotel performance patterns.
        </Typography>
      </Alert>

      {/* Quick Insights */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Quick Insights
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Average</Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                {calculateStats.avgOccupancy.toFixed(1)}%
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Peak</Typography>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                {calculateStats.maxOccupancy.toFixed(1)}%
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Lowest</Typography>
              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                {calculateStats.minOccupancy.toFixed(1)}%
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Data Points</Typography>
              <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                {calculateStats.dataPoints}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Save/Cancel for column deletes */}
      {hasPendingChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
            disabled={isSaving}
            size="small"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setStagedDeletions(new Set());
              setHasPendingChanges(false);
            }}
            disabled={isSaving}
            size="small"
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Add New Item Form */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Add New Occupancy Metric
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Month"
            select
            value={addFormItemName}
            onChange={(e) => setAddFormItemName(e.target.value)}
            size="small"
            required
            sx={{ minWidth: 150 }}
          >
            {months.map((month) => (
              <MenuItem key={month} value={month}>
                {month}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Year"
            value={addFormYear}
            onChange={(e) => setAddFormYear(e.target.value)}
            size="small"
            required
            sx={{ width: 100 }}
          />

          <TextField
            label="Percentage"
            value={addFormAmount}
            onChange={(e) => setAddFormAmount(e.target.value)}
            size="small"
            type="number"
            required
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{ width: 120 }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNewItem}
            disabled={saving || !addFormItemName || !addFormYear || !addFormAmount}
            startIcon={<AddIcon />}
            size="small"
          >
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </Box>
      </Paper>

      {/* DataGrid */}
      <Paper elevation={0} sx={{ width: '100%', borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.default, 0.8)
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Occupancy Metrics Data
          </Typography>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={handleExportCSV}
            size="small"
          >
            Export CSV
          </Button>
        </Box>

        <DataGrid
          rows={transformedRows}
          columns={activeColumns}
          loading={loading || saving || isSaving}
          editMode="cell"
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onProcessRowUpdateError}
          density="compact"
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[25, 50, 100]}
          autoHeight
          sx={{
            width: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.3)',
              fontSize: '0.875rem',
              padding: '8px 16px'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
              minHeight: '40px !important',
              height: '40px !important'
            },
            '& .MuiDataGrid-row': {
              minHeight: '40px !important',
              maxHeight: '40px !important',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)'
              }
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: alpha(theme.palette.background.default, 0.5)
            }
          }}
          onCellClick={(params, event) => {
            // Only start edit mode if the cell is editable and not a checkbox
            if (params.field !== '__check__') {
              // Auto-start edit mode for better UX
              setTimeout(() => {
                if (params.api && params.api.startCellEditMode) {
                  params.api.startCellEditMode({ id: params.id, field: params.field });
                }
              }, 100);
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default OccupancyMetrics;
